import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import MUITypography from '../../../Components/MUITypography';
import { align_center } from '../../../Styles/VMStyles';
import Loader from '../../../Components/Loader';
import { invoice_template_object } from '../../../Util/UtilHelper';
import { withFileAuthToken } from '../../../Util/jwtAuth';

const InvoiceTemplateDrawer = ({ open, onClose, enquiry_id, user_id, name }) => {
  const theme = useTheme();
  // const sessionFactory = new StorageFactory('session');

  /** Get invoice templates based on current enquiry & user */
  const templates = useMemo(
    () => invoice_template_object(enquiry_id, user_id) || [],
    [enquiry_id, user_id]
  );

  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewError, setPreviewError] = useState('');
  const previewObjectUrlRef = useRef(null);

  const releasePreviewUrl = () => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  };

  /** Reset template selection whenever:
   *  - drawer opens
   *  - templates change (new enquiry/user)
   */
  useEffect(() => {
    if (!open || templates.length === 0) {
      setSelectedTemplateId('');
      return;
    }

    const preferred =
      templates.find((t) => t.id === 'template1')?.id || templates[0].id;

    setSelectedTemplateId(preferred);
  }, [open, templates]);

  const selected = templates.find((tpl) => tpl.id === selectedTemplateId);

  const handleSelect = (event) => {
    setSelectedTemplateId(event.target.value);
    setLoading(true);
  };

  /**
   * The preview is a document URL. An iframe cannot send Authorization, so load
   * the invoice with the session token and show the file from a local blob.
   */
  useEffect(() => {
    if (!open || !selected?.previewUrl) {
      releasePreviewUrl();
      setPreviewSrc('');
      setPreviewError('');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setPreviewError('');
    setPreviewSrc('');

    const loadPreview = async () => {
      try {
        const resp = await fetch(selected.previewUrl, { method: 'GET', mode: 'cors' });
        const contentType = resp.headers.get('content-type') || '';

        if (!resp.ok || contentType.includes('json')) {
          let message = 'Unable to load invoice.';
          try {
            const data = await resp.json();
            if (data?.message) message = data.message;
          } catch {
            /* response was not JSON */
          }
          if (!cancelled) setPreviewError(message);
          return;
        }

        const blob = await resp.blob();
        if (cancelled) return;

        releasePreviewUrl();
        const objectUrl = URL.createObjectURL(blob);
        previewObjectUrlRef.current = objectUrl;
        setPreviewSrc(`${objectUrl}#toolbar=0&navpanes=0&scrollbar=0`);
      } catch (err) {
        if (cancelled) return;
        const authedUrl = withFileAuthToken(selected.previewUrl);
        if (authedUrl && authedUrl !== selected.previewUrl) {
          setPreviewSrc(`${authedUrl}#toolbar=0&navpanes=0&scrollbar=0`);
          return;
        }
        console.warn('Invoice preview failed', err);
        setPreviewError('Unable to load invoice.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [open, selected?.previewUrl]);

  useEffect(() => () => releasePreviewUrl(), []);

  /** PDF download button */
  const handleDownload = async () => {
    if (!selected?.previewUrl) return;

    const fileName = `${name}-${enquiry_id}-${selectedTemplateId}.pdf`;
    setLoading(true);

    try {
      const resp = await fetch(selected.previewUrl, { method: 'GET', mode: 'cors' });
      const contentType = resp.headers.get('content-type') || '';

      if (!resp.ok || contentType.includes('json')) {
        throw new Error(`Failed to fetch file: ${resp.status}`);
      }

      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      console.warn('Invoice download failed', err);
      setPreviewError('Unable to download invoice. Please login again and retry.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 'auto',
          minWidth: 1000,
          maxWidth: '90vw',
          backgroundColor: '#fafafa',
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          boxShadow: 6,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 4, pt: 4, pb: 2, position: 'relative', borderBottom: '1px solid #ddd' }}>
        <MUITypography
          variant="h3"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            textAlign: 'center',
          }}
        >
          Choose Invoice Template
        </MUITypography>

        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 16, top: 16 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box
        sx={{
          px: 4,
          pt: 1,
          overflowY: 'auto',
          height: 'calc(100vh - 100px)',
        }}
      >
        {/* Template List */}
        {templates.length > 0 ? (
          <RadioGroup
            row
            value={selectedTemplateId}
            onChange={handleSelect}
            sx={{
              display: 'flex',
              gap: 4,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              px: 1,
              mb: 1,
              '::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {templates.map((template) => (
              <Box
                key={template.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 180,
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.03)' },
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    height: 110,
                    borderRadius: 2,
                    border: '1px solid #D0D0D0',
                    backgroundColor: '#fff',
                    backgroundImage: `url(${template.image}?v=${Date.now()})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    boxShadow:
                      selectedTemplateId === template.id
                        ? `0 0 0 3px ${theme.palette.primary.main}`
                        : '0px 2px 6px rgba(0,0,0,0.06)',
                    marginTop: 2,
                  }}
                />

                <Box sx={{ ...align_center, mt: 1 }}>
                  <FormControlLabel
                    value={template.id}
                    control={<Radio color="primary" />}
                    label={template.title}
                    sx={{
                      backgroundColor: '#fff',
                      border: '1px solid #ccc',
                      borderRadius: 1.5,
                      px: 2,
                      py: 0.5,
                      fontSize: 13,
                      ml: 4,
                      width: 160,
                      textAlign: 'center',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    }}
                  />
                </Box>
              </Box>
            ))}
          </RadioGroup>
        ) : (
          <Typography variant="body1" color="text.secondary">
            No templates available for this enquiry.
          </Typography>
        )}

        {/* Preview & Download */}
        {selected?.previewUrl ? (
          <>
            {/* Download Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Box
                onClick={handleDownload}
                role="button"
                tabIndex={0}
                sx={{
                  px: 2.5,
                  py: 1,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                  borderRadius: 1.5,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#162e66' },
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                Download Invoice
              </Box>
            </Box>

            {/* Preview Frame */}
            <Box
              sx={{
                height: '70vh',
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: '#f3f3f3',
                position: 'relative',
                border: '1px solid #ddd',
              }}
            >
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255,255,255,0.65)',
                    zIndex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Loader loading={loading} />
                </Box>
              )}

              {previewError ? (
                <Box sx={{ p: 3 }}>
                  <Typography color="error">{previewError}</Typography>
                </Box>
              ) : previewSrc ? (
                <iframe
                  key={previewSrc}
                  title={`Invoice Preview - ${selected.title}`}
                  src={previewSrc}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: '#f3f3f3',
                  }}
                />
              ) : null}
            </Box>
          </>
        ) : (
          <Typography color="error" sx={{ mt: 3 }}>
            Unable to preview this template.
          </Typography>
        )}
      </Box>
      
    </Drawer>
  );
};

export default InvoiceTemplateDrawer;
